import { useEffect } from "react";
import { useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import { identitySchema } from "@common/components/helpers/validation-schemas/identitySchema";
import IdentityFormModalAction from "@common/redux/actions/IdentityFormModalAction";
import AlertModalAction from "@common/redux/actions/AlertModalAction";
import CustomerClient from "@common/api/CustomerClient";
import usePhoto from "./usePhoto";
import { stripBase64Prefix } from "../helpers/image";

const useIdentityFormModal = () => {
  const data = useSelector((store) =>
    IdentityFormModalAction.getStatePath(store),
  );
  const {
    searchQuery,
    identityFormData,
    identityFormOptions,
    optionalName,
    isEditing,
  } = data;
  const { photo } = usePhoto();

  const schema = identitySchema(identityFormOptions, optionalName);
  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues: { ...identityFormData, name: searchQuery },
    mode: "all",
  });
  const {
    reset,
    formState: { isValid },
  } = methods;

  // Effect to reset form values when initialValue changes
  useEffect(() => {
    reset({ ...identityFormData, name: searchQuery });
  }, [identityFormData, searchQuery, reset]);

  const closeModal = () => {
    reset();
    IdentityFormModalAction.dispatch({ open: false }).then(
      IdentityFormModalAction.onClose,
    );
  };

  // hasPhoto flag from backend: 0 is Not required, 1 is Optional, 2 is Required
  const isValidPhoto =
    identityFormOptions?.customerConfiguration?.hasPhoto === 2 ? !!photo : true;

  const onSubmit = (data) => {
    if (!isValid || !isValidPhoto) {
      if (!isValidPhoto) console.error("Photo is not valid!");
      return;
    }

    const callback = () => {
      IdentityFormModalAction.onSubmit({
        ...data,
        ...(photo && { image: stripBase64Prefix(photo) }), // Here we set the image for the customer's payload
      });
      reset();
      IdentityFormModalAction.dispatch({ open: false });
    };

    if (!data.phoneNumber || data.phoneNumber.length < 1 || isEditing) {
      callback();
    } else {
      CustomerClient.Search({
        phoneNumber: data.phoneNumber,
      }).then((response) => {
        if (response.totalRecords > 0) {
          AlertModalAction.open(
            "Customer(s) with that phone number already exist - selecting those instead.",
            "Error",
            () => IdentityFormModalAction.onExists(response),
          );

          reset();
          IdentityFormModalAction.reset();
          return;
        }
        callback();
      });
    }
  };

  return { data, methods, closeModal, onSubmit, isValidPhoto };
};

export default useIdentityFormModal;
